import { D as DialogComponent } from "./dialog-Bckxd7dC.js";
import { T as ThemeEvents } from "./events-C4hlH4zR.js";
import { d as defineCustomElement, f as formatMoney } from "./utilities-BToXpjgA.js";
import "./component-BFVg3Q7H.js";
const OPEN_DELAY_MS = 600;
class CartDrawerComponent extends DialogComponent {
  #removingBtn = null;
  #pointerDownTimeout = null;
  #openTimer = null;
  #openedOptimistically = false;
  #closeOnQueueEnd = false;
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("liquid-ajax-cart:request-start", this.#handleRequestStart, {
      signal: this.signal
    });
    document.addEventListener("pointerdown", this.#handlePointerDown, {
      signal: this.signal
    });
    document.addEventListener("liquid-ajax-cart:request-end", this.#handleRequestEnd, {
      signal: this.signal
    });
    document.addEventListener("liquid-ajax-cart:queue-end", this.#handleQueueEnd, {
      signal: this.signal
    });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.signal
    });
    if (window.liquidAjaxCart?.cart) {
      this.#updateCartTotal();
    } else {
      const initHandler = () => {
        this.#updateCartTotal();
        document.removeEventListener("liquid-ajax-cart:init", initHandler);
      };
      document.addEventListener("liquid-ajax-cart:init", initHandler, { signal: this.signal });
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearOpenTimer();
  }
  #handlePointerDown = (event) => {
    const btn = event.target.closest("[data-ajax-cart-request-button]");
    if (!btn) {
      return;
    }
    if (this.#pointerDownTimeout) {
      clearTimeout(this.#pointerDownTimeout);
    }
    this.#removingBtn = btn;
    btn.classList.add("is-removing-pending");
    this.#pointerDownTimeout = setTimeout(() => {
      if (this.#removingBtn === btn) {
        btn.classList.remove("is-removing-pending");
        this.#removingBtn = null;
      }
    }, 500);
  };
  #handleRequestStart = (event) => {
    const { requestState } = event.detail;
    if (this.#pointerDownTimeout) {
      clearTimeout(this.#pointerDownTimeout);
      this.#pointerDownTimeout = null;
    }
    const initiator = requestState.info?.initiator;
    if (initiator?.hasAttribute("data-ajax-cart-request-button")) {
      this.#removingBtn = initiator;
      initiator.classList.remove("is-removing-pending");
      initiator.classList.add("is-removing");
    } else if (this.#removingBtn) {
      this.#removingBtn.classList.remove("is-removing-pending");
      this.#removingBtn.classList.add("is-removing");
    }
    if (requestState.requestType !== "add") {
      return;
    }
    if (this.#isShown) {
      this.dataset.loading = "";
      return;
    }
    if (!this.#openTimer) {
      const itemCount = window.liquidAjaxCart?.cart?.item_count ?? 0;
      this.#openedOptimistically = itemCount === 0;
      this.#openTimer = setTimeout(this.#openWhileLoading, OPEN_DELAY_MS);
    }
  };
  #openWhileLoading = () => {
    this.#openTimer = null;
    if (!window.liquidAjaxCart?.processing) {
      return;
    }
    this.showDialog();
    this.dataset.loading = "";
  };
  #clearOpenTimer() {
    if (this.#openTimer) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
  }
  #handleRequestEnd = (event) => {
    const { requestState } = event.detail;
    if (this.#removingBtn) {
      this.#removingBtn.classList.remove("is-removing", "is-removing-pending");
      this.#removingBtn = null;
    }
    if (requestState.requestType !== "add") {
      return;
    }
    if (!requestState.responseData?.ok) {
      if (this.#openTimer) {
        this.#clearOpenTimer();
        return;
      }
      this.#closeOnQueueEnd = true;
      if (!this.#openedOptimistically) {
        delete this.dataset.loading;
      }
      return;
    }
    this.#clearOpenTimer();
    delete this.dataset.loading;
    this.#closeOnQueueEnd = false;
    if (!this.#isShown) {
      this.showDialog();
    }
  };
  // dialog.open stays true during the close animation; treat a closing drawer as
  // closed so showDialog() can cancel the close.
  get #isShown() {
    return this.refs.dialog.open && !this.refs.dialog.classList.contains("dialog-closing");
  }
  // A fee-less retry from special-sale-selector runs in the same queue, so its
  // success resets #closeOnQueueEnd before this fires. A retry after a failure
  // before the delayed open starts its own timer at its request-start.
  #handleQueueEnd = () => {
    this.#clearOpenTimer();
    const itemCount = window.liquidAjaxCart?.cart?.item_count ?? 0;
    if (this.#closeOnQueueEnd && this.#openedOptimistically && itemCount === 0 && this.#isShown) {
      this.refs.dialog.addEventListener(
        "close",
        () => {
          delete this.dataset.loading;
        },
        { once: true, signal: this.signal }
      );
      this.closeDialog();
    } else if (this.#openedOptimistically) {
      delete this.dataset.loading;
    }
    this.#closeOnQueueEnd = false;
    this.#openedOptimistically = false;
  };
  #handleCartUpdate = (event) => {
    const cart = event.detail?.resource;
    this.#updateCartTotal(cart);
    this.#syncStaticFooter(cart);
  };
  #syncStaticFooter(cart) {
    const isEmpty = (cart?.item_count ?? 0) === 0;
    for (const el of this.querySelectorAll(
      "[data-cart-static-footer], [data-cart-static-upsells]"
    )) {
      if (!isEmpty && el.dataset.upsellsEmpty === "true") {
        continue;
      }
      el.hidden = isEmpty;
    }
  }
  /**
   * Optimistically updates the cart total on cart change. Shipping is free
   * store-wide, so the shipping line is static and needs no JS.
   *
   * @param {Object} [cart] - Optional cart object from event, otherwise uses liquidAjaxCart
   */
  #updateCartTotal(cart) {
    const totalElement = this.querySelector(".js-cart-total");
    if (!totalElement) {
      return;
    }
    let currentTotal = 0;
    if (typeof cart?.total_price === "number") {
      currentTotal = cart.total_price;
    } else if (typeof window.liquidAjaxCart?.cart?.total_price === "number") {
      currentTotal = window.liquidAjaxCart.cart.total_price;
    }
    const locale = this.dataset.locale || "de-DE";
    const currency = this.dataset.currency || "EUR";
    totalElement.textContent = formatMoney(currentTotal, { locale, currency });
  }
}
defineCustomElement("cart-drawer-component", CartDrawerComponent);
